'use client';

import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';

import { remarkDirectiveTags } from '../lib/remark-directive-tags';
import { remarkGithubAlerts } from '../lib/remark-github-alerts';
import { sanitizeSchema } from '../lib/sanitize-schema';
import { baseComponents } from './components/base-components';
import { Details, Summary } from './components/details';
import {
	InfoDirective,
	NoteDirective,
	TipDirective,
	WarningDirective,
} from './components/directive-callout';

/**
 * Рендерер AI-сообщений в чате.
 *
 * Поддерживает:
 * - GFM (CommonMark + таблицы, чек-листы, ~~strikethrough~~, autolinks)
 * - GitHub alerts: `> [!NOTE]`, `> [!TIP]`, `> [!WARNING]`, `> [!IMPORTANT]`, `> [!CAUTION]`
 * - Директивы: `:::note`, `:::tip`, `:::warning`, `:::info`
 * - Нативный HTML: `<details><summary>`, `<sub>`, `<sup>`, `<u>` (через rehype-raw + sanitize)
 *
 * XSS-защита: AI-контент проходит через `rehype-sanitize` с allow-list схемой,
 * см. `lib/sanitize-schema.ts`.
 */
const chatComponents = {
	...baseComponents,
	details: Details,
	summary: Summary,
	'directive-note': NoteDirective,
	'directive-info': InfoDirective,
	'directive-tip': TipDirective,
	'directive-warning': WarningDirective,
} as unknown as Components;

export function ChatMarkdown({ children }: { children: string }) {
	return (
		<div className="markdown-body">
			<ReactMarkdown
				remarkPlugins={[
					remarkGfm,
					remarkGithubAlerts,
					remarkDirective,
					remarkDirectiveTags,
				]}
				rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
				components={chatComponents}
			>
				{children}
			</ReactMarkdown>
		</div>
	);
}
